import SalesRepComponent from "@eisinc/App/Components/SalesRep/SalesRep";
import { xHeaderState } from "@eisinc/App/Store/Reducers";
import { ButtonPresentationProps } from "@eisinc/MobiusOverrides/Button";
import Tooltip from "@eisinc/MobiusOverrides/Tooltip/Tooltip";
import { screen } from "@eisinc/XDL/Helpers";
import iconsObject from "@eisinc/XDL/Icons/eisCommonIcons";
import LogIn from "@eisinc/XDL/Icons/LogIn";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getPageLinkByNodeId } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon/Icon";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    visibilityState = "visibilityState",
    icon = "icon",
    title = "title",
    link = "link",
    includePopup = "includePopup",
    isComponent = "isComponent",
    content = "content",
    component = "component",
    forceAuth = "forceAuth",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.visibilityState]: string;
        [fields.icon]: string;
        [fields.title]: string;
        [fields.link]: LinkFieldValue;
        [fields.includePopup]: boolean;
        [fields.isComponent]: boolean;
        [fields.content]: string;
        [fields.component]: string;
        [fields.forceAuth]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState & xHeaderState, ownProps: OwnProps) => {
    return {
        isSticky: state.xHeader.isSticky,
        session: state.context.session,
        link: getPageLinkByNodeId(state, ownProps.fields?.link?.value),
    };
};

export interface HeaderSignInStyles {
    wrapper?: InjectableCss;
    clickable?: ClickablePresentationProps | ButtonPresentationProps;
    icon?: IconPresentationProps;
    title?: TypographyPresentationProps;
}

export const headerSignInStyles: HeaderSignInStyles = {
    wrapper: {
        css: css`
            display: inline-flex;
        `,
    },
    clickable: {
        css: css`
            // margin-left: 10px;
            display: flex;
            flex-direction: column;
        `,
    },
    icon: {
        color: "text.main",
        css: css`
            margin-bottom: 7px;
            fill: ${getColor("text.main")};
        `,
    },
    title: {
        css: css`
            margin-left: 10px;
            max-width: 150px;
            font-size: 14px;
            color: ${getColor("text.main")};
            ${screen("lte", "md")`display: none;`}
            ${(props: any) => props.sticky && `font-size:12px;`}
        `,
        ellipsis: true,
        transform: "uppercase",
    },
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasHistory;
const styles = headerSignInStyles;
let icon: React.ComponentType;
const HeaderSignIn: FC<Props> = ({ fields, history, isSticky, session, link }) => {
    const showIcon = fields.visibilityState === "both" || fields.visibilityState === "icon";
    const showLabel = fields.visibilityState === "both" || fields.visibilityState === "label";

    if (fields.icon && iconsObject[fields.icon]) {
        icon = iconsObject[fields.icon];
    } else {
        icon = LogIn;
    }

    const goTo = () => {
        if (!link?.url || link?.url === "#") {
            return;
        }
        history.push(link?.url);
    };

    const SignInElement = () => (
        <Clickable {...styles.clickable} onClick={goTo}>
            {showIcon && <Icon {...styles.icon} size={isSticky ? 24 : 30} src={icon} />}
            {showLabel && (
                <Typography {...styles.title} {...({ sticky: isSticky } as any)}>
                    {fields?.title}
                </Typography>
            )}
        </Clickable>
    );

    const SignInElementTrigger = (
        <Clickable {...styles.clickable} onClick={goTo}>
            {showIcon && <Icon {...styles.icon} size={isSticky ? 24 : 30} src={icon} />}
            {showLabel && (
                <Typography {...styles.title} {...({ sticky: isSticky } as any)}>
                    {fields?.title}
                </Typography>
            )}
        </Clickable>
    );

    return (
        <>
            {((fields.forceAuth && session.isAuthenticated) || !fields.forceAuth) && (
                <StyledWrapper {...styles.wrapper}>
                    {fields.includePopup && (
                        <StyledWrapper>
                            <Tooltip
                                trigger="mouseenter focus"
                                placement="bottom"
                                delay={[100, 350]}
                                offset={[0, 8]}
                                showBlackArrow={true}
                                triggerComponent={SignInElementTrigger}
                                cssOverrides={{
                                    tooltipBody: css``,
                                    tooltipContainer: css`
                                        padding: 10px;
                                        min-width: 330px;
                                    `,
                                    tooltipClickable: css``,
                                }}
                                passAsChildren={true}
                            >
                                <StyledWrapper>
                                    {!fields?.isComponent && fields?.content && parse(fields?.content, parserOptions)}
                                    {fields.isComponent && fields?.component === "sales-rep" && (
                                        <SalesRepComponent showTitle={true} withBorders={true} smallImageSize={false} />
                                    )}
                                </StyledWrapper>
                            </Tooltip>
                        </StyledWrapper>
                    )}
                    {!fields.includePopup && <SignInElement />}
                </StyledWrapper>
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withHistory(HeaderSignIn)),
    definition: {
        displayName: "Header Icon Link",
        icon: "User",
        fieldDefinitions: [
            {
                name: fields.visibilityState,
                displayName: "Settings",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Both",
                        value: "both",
                    },
                    {
                        displayName: "Show Label",
                        value: "label",
                    },
                    {
                        displayName: "Show Icon",
                        value: "icon",
                    },
                ],
                defaultValue: "both",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.forceAuth,
                displayName: "On authenticated user only",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.icon,
                displayName: "Icon",
                editorTemplate: "DropDownField",
                options: Object.keys(iconsObject).map(i => ({ displayName: i, value: i })),
                defaultValue: "User",
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                placeholder: "Title",
                sortOrder: 4,
            },
            {
                name: fields.link,
                displayName: "Click Link",
                editorTemplate: "CustomLinkField" as any,
                defaultValue: { value: "#", type: "Page" },
                fieldType: "General",
                sortOrder: 5,
            },
            {
                name: fields.includePopup,
                displayName: "Include Popup",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 6,
            },
            {
                name: fields.isComponent,
                displayName: "Is Component",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 7,
                isVisible: (item: HasFields) => item.fields.includePopup,
            },
            {
                name: fields.content,
                displayName: "Popup Content",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "General",
                placeholder: "your content goes here",
                sortOrder: 8,
                isVisible: (item: HasFields) => item.fields.includePopup && !item.fields.isComponent,
            },
            {
                name: fields.component,
                displayName: "Component",
                editorTemplate: "DropDownField",
                options: [{ value: "sales-rep", displayName: "Sales Rep" }],
                defaultValue: "sales-rep",
                fieldType: "General",
                sortOrder: 9,
                isVisible: (item: HasFields) => item.fields.includePopup && item.fields.isComponent,
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
